import { supabase } from '../supabaseClient';

/**
 * Staff roles and what they open.
 *
 * A company owns its roles. A management role lists the workspace areas it
 * opens; a guard role can carry a default hourly rate. Somebody may hold
 * several management roles and at most one guard role, and the database keeps
 * a summary of that on their profile (`role` as a tier, `is_guard` as a flag)
 * so that everything written before roles existed still reads true.
 *
 * Nothing here decides what anybody may do: `has_permission` in Postgres does,
 * and the helpers below only stop the workspace offering what will be refused.
 */

/** Must match public.staff_permission_keys(). */
export const STAFF_PERMISSIONS = [
  'ops_centre',
  'schedule',
  'alarms',
  'staff',
  'reports',
  'analytics',
  'sites',
  'payroll',
  'clients',
] as const;

export type StaffPermission = typeof STAFF_PERMISSIONS[number];

/** Must match public.default_ops_manager_permissions(). */
export const DEFAULT_OPS_MANAGER_PERMISSIONS: StaffPermission[] = [
  'ops_centre',
  'schedule',
  'alarms',
  'staff',
  'reports',
  'analytics',
  'sites',
  'payroll',
];

/** profiles.role: the summary of somebody's roles that the database keeps. */
export type StaffTier = 'admin' | 'manager' | 'guard';
export type WorkspaceTier = 'admin' | 'manager';
export type RoleKind = 'guard' | 'management';
export type SystemRoleKey = 'guard_rank_1' | 'guard_rank_2' | 'ops_manager' | 'system_admin';

export interface PermissionDefinition {
  key: StaffPermission;
  /** Untranslated; these are the sidebar's own labels. */
  label: string;
  summary: string;
}

/** In sidebar order, so the editor reads like the menu it controls. */
export const PERMISSION_CATALOGUE: PermissionDefinition[] = [
  { key: 'ops_centre', label: 'Ops Centre', summary: 'Home, live view and shift history' },
  { key: 'schedule', label: 'Schedule', summary: 'Duty board, shifts and leave' },
  { key: 'alarms', label: 'Alarms', summary: 'Panic alerts and the response queue' },
  { key: 'staff', label: 'Staff', summary: 'Staff directory, invites, licence checks and guard ranks' },
  { key: 'reports', label: 'Reports', summary: 'Incident reports and summary reports' },
  { key: 'analytics', label: 'Analytics', summary: 'Performance and workload trends' },
  { key: 'sites', label: 'Sites', summary: 'Sites, patrols and posts' },
  { key: 'payroll', label: 'Payroll', summary: 'Timesheets, pay periods and gross pay previews' },
  { key: 'clients', label: 'Clients', summary: 'Client contracts, client portal access and cover requests' },
];

/** A role as it appears on a person: enough to draw a chip. */
export interface StaffRoleRef {
  id: string;
  name: string;
  kind: RoleKind;
  systemKey: SystemRoleKey | null;
  colour: string | null;
}

export interface CompanyRole extends StaffRoleRef {
  description: string | null;
  permissions: StaffPermission[];
  /** null when unset, and also when the caller may not see pay. */
  defaultHourlyRate: number | null;
  sortOrder: number;
  memberCount: number;
}

/** What the signed-in member of staff may reach in the workspace. */
export interface StaffAccess {
  tier: WorkspaceTier;
  isOwner: boolean;
  isGuard: boolean;
  permissions: StaffPermission[];
  roles: StaffRoleRef[];
}

export interface SaveRoleInput {
  /** Omit to create. */
  id?: string;
  name: string;
  description: string | null;
  /** Fixed once the role exists. */
  kind: RoleKind;
  permissions: StaffPermission[];
  defaultHourlyRate: number | null;
  colour: string | null;
}

export interface GuardRolePayDefault {
  guardId: string;
  roleId: string;
  roleName: string;
  hourlyRate: number;
}

const isPermission = (value: unknown): value is StaffPermission => (
  typeof value === 'string' && (STAFF_PERMISSIONS as readonly string[]).includes(value)
);

const toPermissions = (value: unknown): StaffPermission[] => (
  Array.isArray(value) ? STAFF_PERMISSIONS.filter((key) => value.includes(key)) : []
);

const toSystemKey = (value: unknown): SystemRoleKey | null => (
  value === 'guard_rank_1' || value === 'guard_rank_2' || value === 'ops_manager' || value === 'system_admin'
    ? value
    : null
);

export function toStaffRoleRef(row: Record<string, unknown>): StaffRoleRef {
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    kind: row.kind === 'management' ? 'management' : 'guard',
    systemKey: toSystemKey(row.system_key),
    colour: typeof row.colour === 'string' && row.colour ? row.colour : null,
  };
}

export const toStaffRoleRefs = (value: unknown): StaffRoleRef[] => (
  Array.isArray(value)
    ? value
        .filter((row): row is Record<string, unknown> => Boolean(row) && typeof row === 'object')
        .map(toStaffRoleRef)
        .filter((role) => role.id)
    : []
);

function toCompanyRole(row: Record<string, unknown>): CompanyRole {
  const rate = row.default_hourly_rate === null || row.default_hourly_rate === undefined
    ? null
    : Number(row.default_hourly_rate);

  return {
    ...toStaffRoleRef(row),
    description: (row.description as string | null) ?? null,
    permissions: toPermissions(row.permissions),
    defaultHourlyRate: rate !== null && Number.isFinite(rate) ? rate : null,
    sortOrder: Number(row.sort_order ?? 0),
    memberCount: Number(row.member_count ?? 0),
  };
}

/**
 * What a tier could reach before roles existed. Used when the roles
 * themselves cannot be read, so that a portal deployed ahead of its database,
 * or a dropped request, never locks staff out of the workspace. It errs
 * towards offering: the database still refuses whatever is not theirs.
 */
export function defaultAccessForTier(tier: WorkspaceTier): StaffAccess {
  return {
    tier,
    isOwner: false,
    isGuard: false,
    permissions: tier === 'admin' ? [...STAFF_PERMISSIONS] : [...DEFAULT_OPS_MANAGER_PERMISSIONS],
    roles: [],
  };
}

export const isSystemAdministrator = (access: Pick<StaffAccess, 'tier'>): boolean => access.tier === 'admin';

export const hasPermission = (
  access: Pick<StaffAccess, 'tier' | 'permissions'>,
  permission: StaffPermission,
): boolean => access.tier === 'admin' || access.permissions.includes(permission);

export const isSystemAdministratorRole = (role: Pick<StaffRoleRef, 'systemKey'>): boolean => (
  role.systemKey === 'system_admin'
);

/** The tier a set of roles adds up to. Mirrors sync_profile_role_summary. */
export function tierForRoles(roles: Pick<StaffRoleRef, 'kind' | 'systemKey'>[]): StaffTier {
  if (roles.some(isSystemAdministratorRole)) return 'admin';
  if (roles.some((role) => role.kind === 'management')) return 'manager';
  return 'guard';
}

/**
 * Which roles the actor may give somebody. Mirrors set_staff_roles and
 * attach_staff_invite: handing out workspace access is a system
 * administrator's decision, so anybody else is offered guard ranks only.
 */
export function assignableRoles<T extends Pick<StaffRoleRef, 'kind'>>(
  access: Pick<StaffAccess, 'tier' | 'permissions'>,
  roles: T[],
): T[] {
  if (!hasPermission(access, 'staff')) return [];
  return isSystemAdministrator(access) ? roles : roles.filter((role) => role.kind === 'guard');
}

/**
 * Whether the actor may change the roles this person holds at all. Somebody
 * who is not a system administrator may only move a guard between ranks.
 */
export function canEditRolesOf(
  access: Pick<StaffAccess, 'tier' | 'permissions'>,
  target: { role: StaffTier },
): boolean {
  if (!hasPermission(access, 'staff')) return false;
  return isSystemAdministrator(access) || target.role === 'guard';
}

/**
 * Adding or removing one role from a selection. A guard role replaces the
 * guard role already picked, because somebody holds one rank at a time.
 */
export function toggleRoleSelection(
  selectedIds: string[],
  role: Pick<StaffRoleRef, 'id' | 'kind'>,
  allRoles: Pick<StaffRoleRef, 'id' | 'kind'>[],
): string[] {
  if (selectedIds.includes(role.id)) {
    return selectedIds.filter((id) => id !== role.id);
  }
  if (role.kind !== 'guard') return [...selectedIds, role.id];

  const guardRoleIds = new Set(allRoles.filter((item) => item.kind === 'guard').map((item) => item.id));
  return [...selectedIds.filter((id) => !guardRoleIds.has(id)), role.id];
}

export const roleService = {
  /**
   * The caller's own tier, roles and permissions. Never throws: see
   * defaultAccessForTier.
   */
  async fetchCurrentAccess(fallbackTier: WorkspaceTier): Promise<StaffAccess> {
    try {
      const { data, error } = await supabase.rpc('current_staff_access');
      if (error) throw error;

      const payload = data as Record<string, unknown> | null;
      if (!payload) return defaultAccessForTier(fallbackTier);

      const tier: WorkspaceTier = payload.tier === 'admin' ? 'admin' : payload.tier === 'manager' ? 'manager' : fallbackTier;
      return {
        tier,
        isOwner: payload.is_owner === true,
        isGuard: payload.is_guard === true,
        permissions: tier === 'admin' ? [...STAFF_PERMISSIONS] : toPermissions(payload.permissions),
        roles: toStaffRoleRefs(payload.roles),
      };
    } catch (err) {
      console.error('Unable to load staff roles', err);
      return defaultAccessForTier(fallbackTier);
    }
  },

  async fetchRoles(): Promise<CompanyRole[]> {
    const { data, error } = await supabase.rpc('get_company_roles');
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(toCompanyRole);
  },

  /** Returns the id of the role that was created or changed. */
  async saveRole(input: SaveRoleInput): Promise<string> {
    const { data, error } = await supabase.rpc('save_company_role', {
      p_role_id: input.id ?? null,
      p_name: input.name,
      p_description: input.description,
      p_kind: input.kind,
      p_permissions: input.kind === 'management' ? input.permissions.filter(isPermission) : [],
      p_default_hourly_rate: input.kind === 'guard' ? input.defaultHourlyRate : null,
      p_colour: input.colour,
    });
    if (error) throw error;
    return String(data ?? '');
  },

  async deleteRole(roleId: string): Promise<void> {
    const { error } = await supabase.rpc('delete_company_role', { p_role_id: roleId });
    if (error) throw error;
  },

  /** Replaces the whole set of roles somebody holds. */
  async setStaffRoles(userId: string, roleIds: string[]): Promise<void> {
    const { error } = await supabase.rpc('set_staff_roles', {
      p_user_id: userId,
      p_role_ids: roleIds,
    });
    if (error) throw error;
  },

  async transferOwnership(userId: string): Promise<void> {
    const { error } = await supabase.rpc('transfer_company_ownership', { p_user_id: userId });
    if (error) throw error;
  },

  /** Guards whose rank carries a default hourly rate. Needs the payroll permission. */
  async fetchGuardRolePayDefaults(): Promise<GuardRolePayDefault[]> {
    const { data, error } = await supabase.rpc('get_guard_role_pay_defaults');
    if (error) throw error;

    return ((data ?? []) as Record<string, unknown>[])
      .map((row) => ({
        guardId: String(row.guard_id ?? ''),
        roleId: String(row.role_id ?? ''),
        roleName: String(row.role_name ?? ''),
        hourlyRate: Number(row.default_hourly_rate),
      }))
      .filter((row) => row.guardId && Number.isFinite(row.hourlyRate));
  },
};
